import { Inject, Injectable } from '@angular/core';
import { Dispatch } from '@ngxs-labs/dispatch-decorator';
import { Select } from '@ngxs/store';
import { Observable, of } from 'rxjs';

import {
  CreateAgendaFitterMutationVariables,
  CreateFitterMutationVariables,
  DeleteFitterMutationVariables,
  FitterQueryVariables,
  FitterStatisticsQueryVariables,
  FittersQueryVariables,
  SearchFittersQueryVariables,
  SearchMasterFittersQueryVariables,
  UpdateAgendaFitterMutationVariables,
  UpdateFitterMutationVariables,
} from '@sc/data-access-graphql';
import {
  CTAQueryStateType,
  ClientAppId,
  Fitter,
  FitterStatistic,
  WeatherEvent,
} from '@sc/data-access-models';

import { AssetsService } from '@sc/data-access-assets';
import { FittersHttpService } from '@sc/data-access-http-services';
import { CLIENT_APP_ID } from '@sc/data-access-models';
import { TranslatorService } from '@sc/data-access-translation';
import { FeatureFlippingManagerService } from '@sc/feature-flipping';
import { map } from 'rxjs/operators';
import {
  FittersAddedAction,
  FittersClearedAction,
  FittersRemoveAction,
  FittersState,
} from '../../store';
import { CTAQuerySetAction, CTAQueryState } from '../../store/cta-query';
import { DossierSandboxService } from '../dossier';

@Injectable({
  providedIn: 'root',
})
export class FittersSandboxService {
  @Select(FittersState)
  public fitters$: Observable<Fitter[]>;

  @Select(CTAQueryState)
  public ctaQueryState$: Observable<CTAQueryStateType>;

  public weatherEvents$: Observable<WeatherEvent[]>;

  constructor(
    @Inject(CLIENT_APP_ID) private readonly clientAppId: ClientAppId,
    private readonly fittersHttpService: FittersHttpService,
    public featureFlippingManager: FeatureFlippingManagerService,
    public dossierSB: DossierSandboxService,
    public translator: TranslatorService,
    private readonly assetsService: AssetsService
  ) {
    this.weatherEvents$ = this.assetsService.getWeatherEvents();
  }

  @Dispatch()
  public addFitters(
    fitters: Fitter[],
    merge = true
  ): Observable<FittersAddedAction> {
    return of(new FittersAddedAction(fitters, merge));
  }

  @Dispatch()
  public selectFitterById(
    brandId: string,
    fitterId: string
  ): Observable<CTAQuerySetAction> {
    return of(
      new CTAQuerySetAction({ brandId, fitterId, fitterSelectionActive: true })
    );
  }

  @Dispatch()
  public selectFitterByName(fitterName: string) {
    return of(
      new CTAQuerySetAction({ fitterName, fitterSelectionActive: true })
    );
  }

  @Dispatch()
  public unselectFitter(): Observable<CTAQuerySetAction> {
    return of(new CTAQuerySetAction({ fitterSelectionActive: false }));
  }

  @Dispatch()
  public selectLicensePlate(
    licensePlate: string
  ): Observable<CTAQuerySetAction> {
    return of(new CTAQuerySetAction({ licensePlate }));
  }

  @Dispatch()
  public selectBusinessOrigin(
    businessOrigin: string
  ): Observable<CTAQuerySetAction> {
    return of(new CTAQuerySetAction({ businessOrigin }));
  }

  @Dispatch()
  public removeFitters(
    fittersToRemove: string[]
  ): Observable<FittersRemoveAction> {
    return of(new FittersRemoveAction(fittersToRemove));
  }

  public fetchFitter(params: FitterQueryVariables): Observable<Fitter> {
    return this.fittersHttpService.fetchFitter(params);
  }

  @Dispatch()
  public fetchFitters(
    params: FittersQueryVariables
  ): Observable<FittersAddedAction> {
    return this.fittersHttpService
      .fetchFitters(params)
      .pipe(map((fitters) => new FittersAddedAction(fitters)));
  }

  @Dispatch()
  public clearFitters(): Observable<FittersClearedAction> {
    return of(new FittersClearedAction());
  }

  public searchFitters(
    params?: SearchFittersQueryVariables
  ): Observable<Fitter[]> {
    return this.fittersHttpService.searchFitters(params);
  }

  public searchMasterFitters(
    params?: SearchMasterFittersQueryVariables
  ): Observable<Fitter[]> {
    return this.fittersHttpService.searchMasterFitters(params);
  }

  public searchFitterStatistics(
    params?: FitterStatisticsQueryVariables
  ): Observable<FitterStatistic[]> {
    return this.fittersHttpService.searchFitterStatistics(params);
  }

  public createFitter(
    params: CreateFitterMutationVariables
  ): Observable<Fitter> {
    return this.fittersHttpService.createFitter(params);
  }

  public updateFitter(
    params: UpdateFitterMutationVariables
  ): Observable<Fitter> {
    return this.fittersHttpService.updateFitter(params);
  }

  public createAgendaFitter(
    params: CreateAgendaFitterMutationVariables
  ): Observable<Fitter> {
    return this.fittersHttpService.createAgendaFitter(params);
  }

  public updateAgendaFitter(
    params: UpdateAgendaFitterMutationVariables
  ): Observable<Fitter> {
    return this.fittersHttpService.updateAgendaFitter(params);
  }
  public deleteFitter(
    params: DeleteFitterMutationVariables
  ): Observable<boolean> {
    return this.fittersHttpService.deleteFitter(params);
  }

  public uploadFitters(params: {
    file: File;
    brandId: string;
  }): Observable<any> {
    return this.fittersHttpService.uploadFitters(params);
  }

  public uploadFittersStatistic(params: {
    file: File;
    brandId: string;
    period: string;
  }): Observable<any> {
    return this.fittersHttpService.uploadFittersStatistic(params);
  }

  public isItinerant(fitter: Fitter) {
    return fitter?.fitterType === 'MOBILE';
  }

  public isNotItinerant(fitter: Fitter) {
    return fitter?.fitterType !== 'MOBILE';
  }

  public getCompleteAddress(fitter: Fitter) {
    if (!fitter) {
      return '-';
    }

    return this.isItinerant(fitter)
      ? ''
      : this.translator.getInstantTranslation(
          'GlobalApplicativeConstants.fitterAddress',
          {
            address: fitter?.address,
            cp: fitter?.cp ?? '',
            city: fitter?.city ?? '',
          }
        );
  }

  public getFitterIdByName(fitterName: string) {
    return this.fittersHttpService.getFitterIdByName(fitterName);
  }

  public showGoogleReview(brandId?: string) {
    if (
      brandId &&
      this.featureFlippingManager.isBrandFeatureActivated(
        brandId,
        'no-display-google-reviews'
      )
    )
      return false;

    switch (this.clientAppId) {
      case 'customer':
        return this.featureFlippingManager.isFeatureActivated(
          'b2c-display-google-reviews'
        );
      case 'insurer':
        return this.featureFlippingManager.isFeatureActivated(
          'b2b-display-google-reviews'
        );
      default:
        return false;
    }
  }

  getCurrentApp() {
    return this.clientAppId;
  }

  uploadFitterWeather(
    events: WeatherEvent[]
  ): Observable<{ weatherEvents: WeatherEvent[] }> {
    this.weatherEvents$ = of(events);
    return this.fittersHttpService.uploadFitterWeather({
      event: events,
    });
  }
}
